import { useState } from "react";
import type { NavigationNode } from "../lib/types";

interface SubjectOverviewProps {
  subject: NavigationNode;
  onNavigateToPage: (slug: string) => void;
}

export interface SubjectOverviewTopic {
  id: string;
  title: string;
  description: string;
  pages: NavigationNode[];
}

export interface SubjectOverviewData {
  topics: SubjectOverviewTopic[];
  pages: NavigationNode[];
  directPages: NavigationNode[];
}

function pagesBelow(node: NavigationNode): NavigationNode[] {
  return node.type === "PAGE"
    ? (node.pageSlug ? [node] : [])
    : node.children.flatMap(pagesBelow);
}

export function buildSubjectOverview(subject: NavigationNode): SubjectOverviewData {
  const topics = subject.children
    .filter((node) => node.type === "TOPIC")
    .map((topic) => ({
      id: topic.id,
      title: topic.title,
      description: topic.description,
      pages: pagesBelow(topic),
    }))
    .filter((topic) => topic.pages.length > 0);

  return {
    topics,
    pages: pagesBelow(subject),
    directPages: subject.children.filter((node) => node.type === "PAGE" && node.pageSlug),
  };
}

function countLabel(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function ContentGrid({ pages, onNavigateToPage }: { pages: NavigationNode[]; onNavigateToPage: (slug: string) => void }) {
  return (
    <div className="subject-content-grid">
      {pages.map((page) => (
        <button
          className="subject-content-card"
          key={page.id}
          onClick={() => page.pageSlug && onNavigateToPage(page.pageSlug)}
        >
          <span className={`subject-content-icon ${page.pageType === "EXERCISE" ? "exercise" : "learning"}`} aria-hidden="true">
            {page.icon || (page.pageType === "EXERCISE" ? "✏️" : "📖")}
          </span>
          <span className="subject-content-copy">
            <small>{page.pageType === "EXERCISE" ? "Übung" : "Lerninhalt"}</small>
            <strong>{page.title}</strong>
            {page.description && <span>{page.description}</span>}
          </span>
          <b aria-hidden="true">→</b>
        </button>
      ))}
    </div>
  );
}

export function SubjectOverview({ subject, onNavigateToPage }: SubjectOverviewProps) {
  const overview = buildSubjectOverview(subject);
  const [view, setView] = useState<"topics" | "all">("topics");
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const selectedTopic = overview.topics.find((topic) => topic.id === selectedTopicId) ?? null;

  function showTopics() {
    setView("topics");
  }

  function showAllContents() {
    setView("all");
    setSelectedTopicId(null);
  }

  return (
    <div className="subject-overview">
      <section className="subject-overview-hero">
        <span className="subject-overview-icon" aria-hidden="true">{subject.icon || "✏️"}</span>
        <div>
          <p className="eyebrow">Fachansicht</p>
          <h1>{subject.title}</h1>
          {subject.description && <p>{subject.description}</p>}
          <div className="subject-overview-metrics" aria-label="Umfang des Fachs">
            <span>{countLabel(overview.topics.length, "Thema", "Themen")}</span>
            <span>{countLabel(overview.pages.length, "Inhalt", "Inhalte")}</span>
          </div>
        </div>
      </section>

      <div className="subject-overview-tabs" role="group" aria-label="Fachansicht auswählen">
        <button className={view === "topics" ? "active" : ""} aria-pressed={view === "topics"} onClick={showTopics}>Themen entdecken</button>
        <button className={view === "all" ? "active" : ""} aria-pressed={view === "all"} onClick={showAllContents}>Alle Inhalte</button>
      </div>

      {view === "topics" ? (
        <section className="subject-overview-section" aria-labelledby="subject-topics-title">
          <div className="subject-overview-heading">
            <h2 id="subject-topics-title">Themen</h2>
            <p>Wähle ein Thema und öffne die passenden Inhalte.</p>
          </div>
          {overview.topics.length > 0 ? (
            <div className="subject-topic-grid">
              {overview.topics.map((topic) => (
                <button
                  className={topic.id === selectedTopicId ? "subject-topic-card selected" : "subject-topic-card"}
                  key={topic.id}
                  aria-pressed={topic.id === selectedTopicId}
                  onClick={() => setSelectedTopicId(topic.id)}
                >
                  <small>{countLabel(topic.pages.length, "Inhalt", "Inhalte")}</small>
                  <strong>{topic.title}</strong>
                  <span>{topic.description || `Hier findest du ${countLabel(topic.pages.length, "Inhalt", "Inhalte")} zum gezielten Üben und Nachlesen.`}</span>
                  <b>Thema öffnen <span aria-hidden="true">→</span></b>
                </button>
              ))}
            </div>
          ) : (
            <div className="subject-overview-empty">Für dieses Fach sind noch keine Themen angelegt.</div>
          )}

          {selectedTopic && (
            <section className="subject-selected-topic" aria-labelledby="selected-topic-title">
              <div className="subject-selected-topic-heading">
                <div>
                  <p className="eyebrow">Ausgewähltes Thema</p>
                  <h2 id="selected-topic-title">{selectedTopic.title}</h2>
                </div>
                <button className="subject-close-topic" onClick={() => setSelectedTopicId(null)}>Thema schließen</button>
              </div>
              <ContentGrid pages={selectedTopic.pages} onNavigateToPage={onNavigateToPage} />
            </section>
          )}

          {overview.directPages.length > 0 && (
            <section className="subject-direct-contents" aria-labelledby="direct-contents-title">
              <div className="subject-overview-heading compact">
                <h2 id="direct-contents-title">Direkt im Fach</h2>
                <p>Diese Inhalte sind in der Navigation keinem Thema untergeordnet.</p>
              </div>
              <ContentGrid pages={overview.directPages} onNavigateToPage={onNavigateToPage} />
            </section>
          )}
        </section>
      ) : (
        <section className="subject-overview-section" aria-labelledby="subject-contents-title">
          <div className="subject-overview-heading">
            <h2 id="subject-contents-title">Alle Inhalte</h2>
            <p>Alle in der Navigation veröffentlichten Inhalte dieses Fachs auf einen Blick.</p>
          </div>
          {overview.pages.length > 0
            ? <ContentGrid pages={overview.pages} onNavigateToPage={onNavigateToPage} />
            : <div className="subject-overview-empty">Für dieses Fach sind noch keine Inhalte veröffentlicht.</div>}
        </section>
      )}
    </div>
  );
}
